import React, { useState, useEffect } from 'react';
import { 
  Home,
  Clock, 
  Users, 
  CheckCircle,
  AlertCircle,
  Camera,
  CameraOff,
  Mic,
  MicOff,
  Phone,
  MessageCircle,
  Shield,
  Monitor,
  Loader,
  Video,
  MapPin,
  DollarSign,
  Calendar,
  Star,
  Award,
  Hammer
} from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedBadge,
  BrandedAlert 
} from '../../components/branding/BrandedComponents';

interface CustomerWaitingRoomProps {
  companyName?: string;
  companyLogo?: string;
  projectId?: string;
}

interface ProjectStatus {
  projectId: string;
  status: 'new' | 'reviewing' | 'assigned' | 'ready' | 'in_progress' | 'completed';
  customerName: string;
  title: string;
  category: string;
  urgency: string;
  budget?: string;
  address?: string;
  createdAt: string;
  contractorName?: string;
  estimatedResponseTime?: string;
  meetingId?: string;
  contractorSpecialty?: string;
  contractorRating?: number;
  waitTime?: number;
  updatedAt?: string;
}

export const CustomerWaitingRoom: React.FC<CustomerWaitingRoomProps> = ({
  companyName = 'HomeExpert Contractors',
  companyLogo,
  projectId
}) => {
  return (
    <BrandingProvider templateType="business" initialBranding={{ companyName, companyLogo }}>
      <CustomerWaitingRoomContent 
        companyName={companyName}
        companyLogo={companyLogo}
        projectId={projectId}
      />
    </BrandingProvider>
  );
};

const CustomerWaitingRoomContent: React.FC<CustomerWaitingRoomProps> = ({
  companyName = 'HomeExpert Contractors',
  companyLogo,
  projectId
}) => {
  const [projectStatus, setProjectStatus] = useState<ProjectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionTest, setConnectionTest] = useState<'testing' | 'success' | 'failed'>('testing');
  const [cameraPermission, setCameraPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown');
  const [micPermission, setMicPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown');

  // Get project ID from URL if not provided as prop
  const currentProjectId = projectId || window.location.pathname.split('/').pop();

  // Test camera and microphone permissions
  useEffect(() => {
    const testMediaDevices = async () => {
      try {
        setConnectionTest('testing');
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        setCameraPermission('granted');
        setMicPermission('granted');
        setConnectionTest('success');
        // Stop the stream
        stream.getTracks().forEach(track => track.stop());
      } catch (err) {
        console.error('Media device test failed:', err);
        setCameraPermission('denied');
        setMicPermission('denied');
        setConnectionTest('failed');
      }
    };

    testMediaDevices();
  }, []);

  // Poll project status
  useEffect(() => {
    if (!currentProjectId) {
      setError('No project ID provided');
      setIsLoading(false);
      return;
    }

    console.log('🔄 CUSTOMER: Starting status polling for project ID:', currentProjectId);
    
    const pollStatus = async () => {
      try {
        console.log('🔍 CUSTOMER: Polling status for project ID:', currentProjectId);
        const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/homeimprovement/projects/${currentProjectId}/status`);
        
        if (response.ok) {
          const statusData = await response.json();
          console.log('📊 CUSTOMER: Status update received:', statusData);
          setProjectStatus(statusData.project);
          
          // Check if project has been assigned and contractor is ready
          if (statusData.project.status === 'ready' && statusData.project.contractorName && statusData.project.meetingId) {
            console.log('✅ CUSTOMER: Contractor assigned! Attempting to join video consultation...');
            // Auto-redirect to video session
            await joinVideoConsultation(statusData.project);
          }
        } else {
          console.warn('⚠️ CUSTOMER: Status polling failed with status:', response.status);
          const errorData = await response.text();
          setError(`Failed to get project status: ${errorData}`);
        }
      } catch (err) {
        console.error('🔴 CUSTOMER: Error polling status:', err);
        setError(err instanceof Error ? err.message : 'Failed to check project status');
      } finally {
        setIsLoading(false);
      }
    };

    // Initial status check
    pollStatus();
    
    // Poll every 10 seconds (longer than support since home improvement consultations take more time to set up)
    const interval = setInterval(pollStatus, 10000);
    
    return () => {
      console.log('🛑 CUSTOMER: Cleaning up status polling...');
      clearInterval(interval);
    };
  }, [currentProjectId]);

  const joinVideoConsultation = async (project: ProjectStatus) => {
    try {
      console.log('🎬 CUSTOMER: Joining video consultation for project:', project.projectId);
      
      // Extract org ID from current URL to preserve organization context
      const pathname = window.location.pathname;
      const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
      const orgPrefix = orgMatch ? `/${orgMatch[1]}` : '';
      
      // Build customer join URL with proper org prefix
      const baseUrl = window.location.origin;
      const customerJoinUrl = `${baseUrl}${orgPrefix}/templates/homeimprovement/session/customer?roomId=${project.meetingId}&projectId=${project.projectId}&customerName=${encodeURIComponent(project.customerName)}&contractorName=${encodeURIComponent(project.contractorName || 'Contractor')}&projectTitle=${encodeURIComponent(project.title)}&category=${project.category}&urgency=${project.urgency}`;
      
      console.log('🔗 CUSTOMER: Org prefix:', orgPrefix);
      console.log('🔗 CUSTOMER: Redirecting to video consultation:', customerJoinUrl);
      
      // Redirect to video session
      window.location.href = customerJoinUrl;
      
    } catch (err) {
      console.error('🔴 CUSTOMER: Error joining video consultation:', err);
      setError('Failed to join video consultation. Please try again.');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'kitchen': return '🍳';
      case 'bathroom': return '🛁';
      case 'roofing': return '🏠';
      case 'flooring': return '🪵';
      case 'painting': return '🎨';
      case 'electrical': return '⚡';
      case 'plumbing': return '🔧';
      case 'hvac': return '❄️';
      case 'landscaping': return '🌳';
      default: return '🔨';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'emergency': return 'error';
      case 'urgent': return 'warning';
      case 'soon': return 'primary';
      case 'planning': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'new': return 'Your project has been received and is being reviewed';
      case 'reviewing': return 'Our team is reviewing your project details';
      case 'assigned': return 'A contractor has been assigned to your project';
      case 'ready': return 'Your contractor is ready to discuss your project';
      case 'in_progress': return 'Consultation in progress';
      default: return 'Processing your project request';
    }
  };

  if (error && !projectStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--brand-background)' }}>
        <BrandedCard className="max-w-md w-full text-center">
          <AlertCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-error)' }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--brand-text-primary)' }}>
            Unable to Load Project
          </h2>
          <p className="mb-6" style={{ color: 'var(--brand-text-secondary)' }}>
            {error}
          </p>
          <BrandedButton
            onClick={() => window.location.href = '/templates/homeimprovement/customer'}
            variant="primary"
          >
            Submit New Project
          </BrandedButton>
        </BrandedCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-background)' }}>
      {/* Mobile-Optimized Header */}
      <header className="sticky top-0 z-10 border-b backdrop-blur-sm bg-opacity-95" style={{ 
        backgroundColor: 'var(--brand-surface)', 
        borderColor: 'var(--brand-border)' 
      }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <BrandedHeader 
            showLogo={true} 
            showTagline={true}
            customTagline="Your project consultation is being prepared"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Project Status */}
          <div className="lg:col-span-2 space-y-6">
            <BrandedCard>
              <div className="flex items-center mb-6">
                <Home size={24} className="mr-3" style={{ color: 'var(--brand-primary)' }} />
                <h2 className="text-xl font-semibold" style={{ color: 'var(--brand-text-primary)' }}>
                  Project Consultation Status
                </h2>
              </div>

              {isLoading && !projectStatus ? (
                <div className="text-center py-8">
                  <Loader className="animate-spin mx-auto mb-4" size={32} style={{ color: 'var(--brand-primary)' }} />
                  <p style={{ color: 'var(--brand-text-secondary)' }}>Loading your project status...</p>
                </div>
              ) : projectStatus ? (
                <div className="space-y-6">
                  {/* Project Info */}
                  <div className="border rounded-lg p-4" style={{ borderColor: 'var(--brand-border)' }}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-2xl">{getCategoryIcon(projectStatus.category)}</span>
                          <h3 className="font-semibold text-lg" style={{ color: 'var(--brand-text-primary)' }}>
                            {projectStatus.title}
                          </h3>
                          <BrandedBadge variant={getUrgencyColor(projectStatus.urgency)} size="small">
                            {projectStatus.urgency.toUpperCase()}
                          </BrandedBadge>
                        </div>
                        <p className="text-sm mb-2 flex items-center" style={{ color: 'var(--brand-text-secondary)' }}>
                          <MapPin size={14} className="mr-1" />
                          {projectStatus.address}
                        </p>
                        <p className="text-sm mb-2" style={{ color: 'var(--brand-text-secondary)' }}>
                          Project #{projectStatus.projectId}
                        </p>
                        {projectStatus.budget && (
                          <p className="text-sm flex items-center" style={{ color: 'var(--brand-text-secondary)' }}>
                            <DollarSign size={14} className="mr-1" />
                            Budget: {projectStatus.budget.replace('-', ' - $').replace('k', ',000')}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="border-t pt-4" style={{ borderColor: 'var(--brand-border)' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {projectStatus.status === 'new' || projectStatus.status === 'reviewing' ? (
                            <>
                              <Clock size={16} style={{ color: 'var(--brand-warning)' }} />
                              <span className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                                Under Review
                              </span>
                            </>
                          ) : projectStatus.status === 'assigned' || projectStatus.status === 'ready' ? (
                            <>
                              <CheckCircle size={16} style={{ color: 'var(--brand-success)' }} />
                              <span className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                                Contractor Assigned: {projectStatus.contractorName}
                              </span>
                            </>
                          ) : (
                            <>
                              <Video size={16} style={{ color: 'var(--brand-primary)' }} />
                              <span className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                                In Consultation
                              </span>
                            </>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                            Wait time: {projectStatus.waitTime} minutes
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contractor Info */}
                  {projectStatus.contractorName && (
                    <div className="border rounded-lg p-4" style={{ borderColor: 'var(--brand-success)', backgroundColor: 'var(--brand-surface)' }}>
                      <h4 className="font-semibold mb-3 flex items-center" style={{ color: 'var(--brand-text-primary)' }}>
                        <Award size={18} className="mr-2" style={{ color: 'var(--brand-success)' }} />
                        Your Assigned Contractor
                      </h4>
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                          <Hammer size={20} style={{ color: 'var(--brand-primary)' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                            {projectStatus.contractorName}
                          </p>
                          {projectStatus.contractorSpecialty && (
                            <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                              Specialty: {projectStatus.contractorSpecialty}
                            </p>
                          )}
                          {projectStatus.contractorRating && (
                            <div className="flex items-center mt-1">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  size={14} 
                                  className={i < projectStatus.contractorRating! ? 'text-yellow-400 fill-current' : 'text-gray-300'} 
                                />
                              ))}
                              <span className="text-xs ml-1" style={{ color: 'var(--brand-text-secondary)' }}>
                                {projectStatus.contractorRating}/5
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Updates */}
                  <BrandedAlert variant={projectStatus.status === 'ready' ? 'success' : 'info'}>
                    <div className="flex items-center">
                      {projectStatus.status === 'ready' ? (
                        <CheckCircle size={16} className="mr-2" />
                      ) : (
                        <Clock size={16} className="mr-2" />
                      )}
                      <span>{getStatusMessage(projectStatus.status)}</span>
                    </div>
                  </BrandedAlert>
                </div>
              ) : null}
            </BrandedCard>

            {/* Connection Test Results */}
            <BrandedCard>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                Video Consultation Readiness
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {cameraPermission === 'granted' ? <Camera size={16} /> : <CameraOff size={16} />}
                    <span className="text-sm">Camera Access</span>
                  </div>
                  <BrandedBadge 
                    variant={cameraPermission === 'granted' ? 'success' : cameraPermission === 'denied' ? 'error' : 'secondary'}
                    size="small"
                  >
                    {cameraPermission === 'granted' ? 'Ready' : cameraPermission === 'denied' ? 'Blocked' : 'Testing...'}
                  </BrandedBadge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {micPermission === 'granted' ? <Mic size={16} /> : <MicOff size={16} />}
                    <span className="text-sm">Microphone Access</span>
                  </div>
                  <BrandedBadge 
                    variant={micPermission === 'granted' ? 'success' : micPermission === 'denied' ? 'error' : 'secondary'}
                    size="small"
                  >
                    {micPermission === 'granted' ? 'Ready' : micPermission === 'denied' ? 'Blocked' : 'Testing...'}
                  </BrandedBadge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Monitor size={16} />
                    <span className="text-sm">Connection</span>
                  </div>
                  <BrandedBadge 
                    variant={connectionTest === 'success' ? 'success' : connectionTest === 'failed' ? 'error' : 'secondary'}
                    size="small"
                  >
                    {connectionTest === 'success' ? 'Good' : connectionTest === 'failed' ? 'Issues' : 'Testing...'}
                  </BrandedBadge>
                </div>
              </div>

              {(cameraPermission === 'denied' || micPermission === 'denied') && (
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm" style={{ color: 'var(--brand-warning)' }}>
                    Please allow camera and microphone access for the video consultation. 
                    Click the camera icon in your browser's address bar to adjust permissions.
                  </p>
                </div>
              )}
            </BrandedCard>
          </div>

          {/* Right Column - What to Expect */}
          <div className="space-y-6">
            <BrandedCard>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                What to Expect
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <CheckCircle size={16} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Free consultation with licensed contractor
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle size={16} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Detailed project assessment and timeline
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle size={16} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Accurate cost estimate and materials list
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle size={16} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Answers to all your project questions
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle size={16} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Next steps and contract options
                  </span>
                </div>
              </div>
            </BrandedCard>

            <BrandedCard>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                Need Help Now?
              </h3>
              <div className="space-y-3">
                <BrandedButton variant="outline" size="small" className="w-full justify-start">
                  <MessageCircle size={14} className="mr-2" />
                  Chat with Support
                </BrandedButton>
                <BrandedButton variant="outline" size="small" className="w-full justify-start">
                  <Phone size={14} className="mr-2" />
                  Call: 1-800-HOME-PRO
                </BrandedButton>
              </div>
            </BrandedCard>

            <BrandedCard>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                Privacy & Security
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-start space-x-2">
                  <Shield size={14} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Licensed & insured contractors
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <Shield size={14} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Secure video consultations
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <Shield size={14} className="mt-0.5" style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>
                    Your information stays private
                  </span>
                </div>
              </div>
            </BrandedCard>
          </div>
        </div>
      </main>
    </div>
  );
};